<script lang="ts">
  type Configuration = { url: string; color: string };

  const CONFIGURATIONS: { [key: string]: Configuration } = {
    "b.arch": {
      url: "https://www.commonapp.org/explore/southern-california-institute-architecture",
      color: "rgb(213, 251, 81)",
    },
    "m.arch": {
      url: "https://apply.sciarc.edu/apply",
      color: "rgb(156, 255, 163)",
    },
    edge: {
      url: "https://apply.sciarc.edu/apply",
      color: "rgb(82, 171, 131)",
    },
  };

  const program =
    window.ApplyNow?.program ||
    window.document.currentScript.getAttribute("data-program");
  const config: Configuration = CONFIGURATIONS[program] || {
    url: "https://apply.sciarc.edu/apply",
    color: "rgb(255, 113, 189)",
  };

  let animationStarted: number | undefined;
  let hovered = false;
  let scale = 0;
  const scaleMax = 15;
  const scaleDuration = 250;
  const deltaPerMillisecond = scaleMax / scaleDuration;
  const scaleClamp = (value: number) => Math.max(0, Math.min(scaleMax, value));

  $: {
    if ((scale < scaleMax && hovered) || (scale > 0 && !hovered)) {
      window.requestAnimationFrame(() => {
        if (animationStarted) {
          const deltaMilliseconds = new Date().getTime() - animationStarted;
          const direction = hovered ? 1 : -1;
          const scaleDelta =
            direction * deltaMilliseconds * deltaPerMillisecond;
          scale = scaleClamp(scale + scaleDelta);
        }
        animationStarted = new Date().getTime();
      });
    } else {
      animationStarted = undefined;
    }
  }
</script>

<svg>
  <filter id="apply-now-warp" filterRes="10">
    <feTurbulence
      type="fractalNoise"
      baseFrequency="0.03"
      numOctaves="1"
      result="waves"
    />
    <feColorMatrix in="waves" type="hueRotate">
      <animate
        attributeName="values"
        values="0;360"
        dur="15s"
        repeatCount="indefinite"
      ></animate>
    </feColorMatrix>
    <feDisplacementMap
      xChannelSelector="R"
      yChannelSelector="G"
      in="SourceGraphic"
      scale={scale.toFixed(3)}
      result="warping"
    />
  </filter>
</svg>

<a
  aria-label="Apply Now"
  class="container"
  href={config.url}
  rel="noreferrer"
  target="_blank"
  tabindex="0"
  on:focus={() => {
    hovered = true;
  }}
  on:blur={() => {
    hovered = false;
  }}
  on:mouseenter={() => {
    hovered = true;
  }}
  on:mouseleave={() => {
    hovered = false;
  }}
>
  <div class="wobble-1">
    <div class="wobble-2">
      <div class="wobble-3">
        <div class="warp">
          <div class="button" style="background-color: {config.color}">
            Apply Now
          </div>
        </div>
      </div>
    </div>
  </div>
</a>

<style>
  .container {
    bottom: 11px;
    left: 50%;
    position: fixed;
    transform: translateX(-50%);
    z-index: 99999999;
    text-decoration: none;
  }

  .container:focus {
    outline: 0;
  }

  .container:focus .button {
    outline: auto;
  }

  .button {
    color: rgb(25, 25, 25);
    font-family: px-grotesk-light, courier;
    font-size: 22px;
    text-transform: uppercase;
    white-space: nowrap;
    text-align: center;
    border: 2px solid rgb(25, 25, 25);
    padding: 5px 10px 4px 9px;
    transition: all 400ms;
    cursor: pointer;
  }

  @supports not (filter: url(#apply-now-warp)) {
    .container:hover .button {
      background-color: rgb(25, 25, 25) !important;
      color: rgb(255, 255, 255);
      border-color: rgb(255, 255, 255);
    }
  }

  .warp {
    filter: url(#apply-now-warp);
    padding: 5px 15px;
  }

  .wobble-1 {
    animation: wobble-1 20s infinite ease-in-out;
  }

  .wobble-2 {
    animation: lateral 12s infinite ease-in-out;
  }

  .wobble-3 {
    animation: vertical 15s infinite ease-in-out;
  }

  @media (prefers-reduced-motion) {
    .wobble-1,
    .wobble-2,
    .wobble-3 {
      animation: none;
    }
  }

  @media screen and (min-width: 640px) {
    .container {
      bottom: 15px;
      left: 12.5%;
    }
  }

  @media screen and (min-width: 1024px) {
    .container {
      font-size: 27px;
      left: 10%;
    }
  }

  @keyframes wobble-1 {
    0% {
      transform: rotate(3deg);
    }

    50% {
      transform: rotate(-3deg);
    }

    100% {
      transform: rotate(3deg);
    }
  }

  @keyframes lateral {
    0% {
      transform: translateX(-3px);
    }

    50% {
      transform: translateX(3px);
    }

    100% {
      transform: translateX(-3px);
    }
  }

  @keyframes vertical {
    0% {
      transform: translateY(-3px);
    }

    50% {
      transform: translateY(3px);
    }

    100% {
      transform: translateY(-3px);
    }
  }
</style>
